import {
    answerToGqlAnswer,
    ICommunity,
    ILocation,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { Community, CommunityCardBasicsFragment } from "../../generated/graphql";
import { ListableCommunity } from "./props";

export const communityAvailabilityToGql = (
    availability: ICommunity["availability"],
): ListableCommunity["availability"] => {
    if (!availability) {
        return null;
    }
    return {
        status: availability.status,
        notes: availability.notes ?? null,
        freshness: availability.freshness,
        lastUpdatedAt: availability.lastUpdatedAt ?? null,
    };
};

export const communityPricingToGql = (
    pricing: ICommunity["pricing"],
): ListableCommunity["pricing"] => {
    if (!pricing) {
        return null;
    }
    return {
        minPrice: pricing.minPrice ?? null,
        maxPrice: pricing.maxPrice ?? null,
        notes: pricing.notes ?? null,
        files: pricing.files ?? null,
    };
};

type Parts = Pick<Community, "id" | "name" | "latitude" | "longitude" | "contractStatus">;
export const gqlCommunityToMappableCommunity = (input: Parts): ListableCommunity => {
    return {
        address: "",
        contacts: [],
        primaryContacts: [],
        contractStatus: input.contractStatus,
        id: input.id,
        lat: input.latitude,
        lng: input.longitude,
        mostRecentPlacement: null,
        name: input.name,
        phone: toPhoneNumberE164(""),
        placements: 0,
        rating: 0,
        mainPhone: toPhoneNumberE164(""),
        summary: "",
        thumbnail: "",
        url: "",
        showInSearchResults: true,
        city: "",
        state: "",
        answers: [],
        referralCount: 0,
        isConnectedToEvergreen: false,
        googleInfo: null,
        availability: null,
        deleted: false,
        matchedAliases: [],
        evergreenAliases: [],
        contractExpiresDate: null,
        contractIssuedDate: null,
        contractNotes: null,
        paymentFee: null,
        paymentPercentage: null,
        gender: null,
        licenseStatus: null,
        unitsOrCapacity: null,
        monthlyPrice: null,
        priceHighEnd: null,
        zip: null,
        feeType: null,
        secondPersonFee: null,
        petDeposit: null,
        moveInDeposit: null,
        priceDetails: null,
        caregiverRatio: null,
        updatedAt: null,
        dateAdded: null,
        regionId: null,
        email: null,
        county: null,
        pricing: null,
        licenses: null,
    };
};

export const communityToListableCommunity = (input: ICommunity): ListableCommunity => {
    const result: ListableCommunity = {
        address: input.address,
        contacts: input.contactEntities,
        primaryContacts: input.primaryContactEntities,
        contractStatus: input.contractStatus,
        id: input.id,
        lat: input?.location?.loc?.[1].toString(),
        lng: input?.location?.loc?.[0].toString(),
        mostRecentPlacement: null,
        name: input.name,
        phone: input.mainPhone,
        placements: 0,
        rating: input.rating,
        mainPhone: input.mainPhone,
        summary: input.summary,
        thumbnail: input.thumbnailUrl,
        url: input.website,
        showInSearchResults: input.showInSearchResults,
        city: input.city,
        state: input.state,
        zip: input.zip,
        answers: (input.answers || []).map(a => answerToGqlAnswer(a)),
        referralCount: 0,
        email: input.email,
        county: input.county,
        isConnectedToEvergreen: input.isConnectedToEvergreen,
        availability: communityAvailabilityToGql(input.availability),
        pricing: communityPricingToGql(input.pricing),
        deleted: false,
        googleInfo: null,
        matchedAliases: [],
        evergreenAliases: [],
        contractExpiresDate: input.contractExpiresDate,
        contractIssuedDate: input.contractIssuedDate,
        contractNotes: input.contractNotes,
        paymentFee: input.paymentFee,
        paymentPercentage: input.paymentPercentage,
        gender: input.gender,
        licenseStatus: input.licenseStatus,
        unitsOrCapacity: input.unitsOrCapacity,
        monthlyPrice: input.monthlyPrice?.toString(),
        priceHighEnd: input.priceHighEnd?.toString(),
        feeType: input.feeType,
        secondPersonFee: input.secondPersonFee,
        petDeposit: input.petDeposit,
        moveInDeposit: input.moveInDeposit?.toString(),
        priceDetails: input.priceDetails,
        caregiverRatio: input.caregiverRatio,
        updatedAt: input.updatedAt,
        dateAdded: input.dateAdded,
        regionId: input.regionId,
        licenses: input.licenses,
    };
    return result;
};

export const locationFor = (l: ListableCommunity): ILocation | null => {
    if (
        !l ||
        l.lat === undefined ||
        l.lng === undefined ||
        l.lng === null ||
        l.lat === null ||
        isNaN(parseFloat(l.lng)) ||
        isNaN(parseFloat(l.lat))
    )
        return null;
    return {
        loc: [parseFloat(l.lng), parseFloat(l.lat)],
    };
};

export const communityCardContactToListableCommunityContact = (
    input: CommunityCardBasicsFragment["contacts"][0],
): ListableCommunity["contacts"][0] => {
    return {
        cellPhone: input.cellPhone,
        name: input.name,
        phone1: input.phone1,
        email1: input.email1,
        role: input.role,
    };
};

export const cardBasicsToListableCommunity = (
    input: CommunityCardBasicsFragment,
): ListableCommunity => {
    const result: ListableCommunity = {
        showInSearchResults: true,
        address: input.address,
        contacts: input.contacts.map(c =>
            communityCardContactToListableCommunityContact(c),
        ),
        primaryContacts: input.primaryContacts.map(c =>
            communityCardContactToListableCommunityContact(c),
        ),
        contractStatus: input.contractStatus,
        id: input.id,
        lat: input.latitude,
        lng: input.longitude,
        mostRecentPlacement: null,
        name: input.name,
        phone: input.mainPhone,
        placements: 0,
        rating: input.rating,
        mainPhone: input.mainPhone,
        summary: input.summary,
        thumbnail: input.thumbnailUrl,
        url: input.website,
        city: input.city,
        state: input.state,
        zip: input.zip,
        answers: input.answers,
        unitsOrCapacity: input.unitsOrCapacity,
        contractExpiresDate: input.contractExpiresDate,
        contractIssuedDate: input.contractIssuedDate,
        contractNotes: input.contractNotes,
        gender: input.gender,
        paymentFee: input.paymentFee,
        paymentPercentage: input.paymentPercentage,
        licenseStatus: input.licenseStatus,
        monthlyPrice: input.monthlyPrice?.toString(),
        priceHighEnd: input.priceHighEnd?.toString(),
        feeType: input.feeType,
        secondPersonFee: input.secondPersonFee,
        petDeposit: input.petDeposit,
        moveInDeposit: input.moveInDeposit?.toString(),
        priceDetails: input.priceDetails,
        caregiverRatio: input.caregiverRatio,
        updatedAt: input.updatedAt,
        dateAdded: input.dateAdded,
        regionId: input.regionId,
        referralCount: 0,
        licenses: input.licenses,
        email: input.email,
        county: input.county,
        isConnectedToEvergreen: input.isConnectedToEvergreen,
        availability: input.availability,
        pricing: input.pricing,
        deleted: false,
        googleInfo: input.googleInfo,
        matchedAliases: [],
        evergreenAliases: [],
    };
    return result;
};
